import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Star, 
  BadgeCheck, 
  MapPin, 
  Calendar,
  TrendingUp,
  ShoppingCart
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProfile = async () => {
    try {
      const [profileRes, ratingsRes] = await Promise.all([
        axios.get(`${API}/users/${id}/public`),
        axios.get(`${API}/ratings/user/${id}`)
      ]);
      
      setProfile(profileRes.data);
      setRatings(ratingsRes.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const gradeColors = {
    'Nouveau': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    'Fiable': 'bg-primary/20 text-primary border-primary/30',
    'Premium': 'bg-violet-500/20 text-violet-500 border-violet-500/30',
    'Elite': 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl font-bold">Profil non trouvé</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-24">
        {/* Profile Header */}
        <Card className="card-dashboard mb-8">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <span className="text-4xl font-bold text-white">
                  {profile.id?.substring(0, 2).toUpperCase()}
                </span>
              </div>
              
              <div className="flex-1 text-center md:text-left">
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-2">
                  <h1 className="font-heading text-2xl font-bold font-mono">
                    KK-{profile.id?.substring(0, 8).toUpperCase()}
                  </h1>
                  {profile.profile_verified && (
                    <BadgeCheck className="w-6 h-6 text-green-500" />
                  )}
                  <Badge className={gradeColors[profile.grade]}>
                    {profile.grade}
                  </Badge>
                </div>
                
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {profile.country}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Membre depuis {new Date(profile.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                  </span>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-2xl font-bold">
                  <Star className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                  {profile.seller_rating?.toFixed(1) || '0.0'}
                </div>
                <p className="text-sm text-muted-foreground">Note vendeur</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-2xl font-bold">
                  <Star className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                  {profile.buyer_rating?.toFixed(1) || '0.0'}
                </div>
                <p className="text-sm text-muted-foreground">Note acheteur</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-2xl font-bold text-green-500">
                  <TrendingUp className="w-5 h-5" />
                  {profile.total_sales}
                </div>
                <p className="text-sm text-muted-foreground">Ventes</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-2xl font-bold text-secondary">
                  <ShoppingCart className="w-5 h-5" />
                  {profile.total_purchases}
                </div>
                <p className="text-sm text-muted-foreground">Achats</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ratings */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle>Avis ({ratings.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {ratings.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Aucun avis pour le moment
              </p>
            ) : (
              <div className="space-y-4">
                {ratings.map((rating) => (
                  <div key={rating.id} className="border-b pb-4 last:border-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= rating.rating
                                  ? 'fill-yellow-500 text-yellow-500'
                                  : 'text-muted-foreground'
                              }`}
                            />
                          ))}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {rating.rating_type === 'seller' ? 'Vendeur' : 'Acheteur'}
                        </Badge>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {new Date(rating.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    {rating.comment && (
                      <p className="text-muted-foreground">{rating.comment}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default ProfilePage;
